import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
} from 'react-router-dom';
import {ChakraProvider, extendTheme} from '@chakra-ui/react';

import { User } from "@/pbgen/v1/main_pb"
import {GlobalContext, GlobalState} from "@/auth"

import { Home } from "@/components/Home"
import { Menu } from "@/components/Menu"
import { Admins, Admin } from "@/components/Admins"
import { Vendors, Vendor } from "@/components/Vendors"
import { Teachers, Teacher } from "@/components/Teachers"
import { Students, Student } from "@/components/Students"
import { Schoolrooms, SchoolroomPage } from "@/components/Schoolrooms"
import { Registration } from "@/components/Registration"
import { Coursewares } from "@/components/Coursewares"
import { Works, AddWorks } from "@/components/Works"
import { GroupWorks } from "@/components/GroupWorks"
import { Login } from "@/components/Login"
import { Mlogin } from "@/components/Mlogin"
import { Ql } from "@/components/Ql"
import { Logout } from "@/components/Logout"
import { Mlogout } from "@/components/Mlogout"
import { Scratch } from "@/components/Scratch"
import { Logs } from "@/components/Logs"
import { Player } from "@/components/Player"
import { Protected } from "@/components/Protected"
// import { Projects } from "@/components/Projects"
import { defineStyle, defineStyleConfig } from '@chakra-ui/react'
import { mode } from "@chakra-ui/theme-tools"

const outline = defineStyle({
  border: '2px dashed', // change the appearance of the border
  borderRadius: 0, // remove the border radius
  fontWeight: 'semibold', // change the font weight
})

export const buttonTheme = defineStyleConfig({
  variants: { outline },
})

const mainColor = '#df6694'
const highlightColor = '#f6dfeb'

const activeLabelStyles = {
  // transform: "scale(0.85) translateY(-24px)"
  transform: "scale(0.85) translateX(-24px) translateY(+24px)",
  backgroundColor: "white",
  zIndex: 2,
};

const theme = extendTheme({
  components: {
    // https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/theme/src/components/button.ts
    Button: {
      variants: {
        solid: {
          color: 'white',
          bg: mainColor,
          _hover: {
            bg: highlightColor,
          }
        },
      },
    },
    // https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/theme/src/components/input.ts
    Input: {
      variants: {
        outline: {
          field: {
            borderColor: mainColor,
            _hover: {
              borderColor: mainColor,
            },
            _focusVisible: {
              borderColor: mainColor,
              boxShadow: `0 0 0 1px ${mainColor}`,
            },
          },
        },
      },
    },
    Textarea: {
      variants: {
        outline: {
          borderColor: mainColor,
          _hover: {
            borderColor: mainColor,
          },
          _focusVisible: {
            borderColor: mainColor,
            boxShadow: `0 0 0 1px ${mainColor}`,
          },
        }
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            borderColor: mainColor,
            _hover: {
              borderColor: mainColor,
            },
            _focusVisible: {
              borderColor: mainColor,
              boxShadow: `0 0 0 1px ${mainColor}`,
            },
          },
        },
      },
    },
    Divider: {
      variants: {
        solid: {
          borderColor: mainColor,
        },
      },
    },
    Form: {
      baseStyle: {
        container: {
          // _focusWithin: {
          //   label: {
          //     ...activeLabelStyles
          //   }
          // },
          // "input:not(:placeholder-shown) + label, .chakra-select__wrapper + label, textarea:not(:placeholder-shown) ~ label": {
          //   ...activeLabelStyles
          // },
          // zIndex: 1, // 0 で、Menu より下へ？ いや、これはいろいろ壊すな。DatePicker の zIndex よりも上へ行ってしまう
          label: {
            // ...activeLabelStyles
            top: 0,
            left: 0,
            zIndex: 2, // 2 で、active 時の枠より上へ行くのか？
            position: "absolute",
            backgroundColor: "white",
            pointerEvents: "none",
            mx: 3,
            px: 1,
            my: -2.5,
            fontSize: "75%",
            transformOrigin: "left top"
          }
        }
      },
    },
    Menu: {
      baseStyle: {
        list: {
          zIndex: 3, // メニューの zIndex をラベルより高く設定
        },
      },
    },
  },
});

const App: React.FunctionComponent = () => {
  const [globalState, setGlobalState] = React.useState({
    currentUser: new User(),
  });
  const globalContext = {
    globalState: globalState,
    setGlobalState: (globalState: GlobalState) => {
      setGlobalState(globalState);
    },
  };
  return (
    // 末尾の slash は無い方が良いようだ
    <BrowserRouter basename={"/ap"}><ChakraProvider theme={theme}><GlobalContext.Provider value={globalContext}><Routes>
      <Route path="/" element={<Home></Home>}></Route>
      <Route path={"/login"} element={<Login/>}></Route>
      <Route path={"/mlogin"} element={<Mlogin></Mlogin>}></Route>
      <Route path={"/ql/:param"} element={<Ql/>}></Route>
      <Route path={"/logout"} element={<Logout></Logout>}></Route>
      <Route path={"/mlogout"} element={<Mlogout></Mlogout>}></Route>
      <Route path="/manage" element={<Menu></Menu>}></Route>
      <Route path="/manage/admins" element={<Admins></Admins>}></Route>
      <Route path="/manage/admins/:aid" element={<Admin></Admin>}></Route>
      <Route path="/manage/vendors" element={<Vendors></Vendors>}></Route>
      <Route path="/manage/vendors/:vid" element={<Vendor></Vendor>}></Route>
      <Route path="/manage/vendors/:vid/schoolrooms" element={<Schoolrooms></Schoolrooms>}></Route>
      <Route path="/manage/vendors/:vid/schoolrooms/:rid" element={<SchoolroomPage></SchoolroomPage>}></Route>
      <Route path="/manage/vendors/:vid/schoolrooms/:rid/students" element={<Students></Students>}></Route>
      <Route path="/manage/vendors/:vid/schoolrooms/:rid/students/:sid" element={<Student></Student>}></Route>
      <Route path="/manage/vendors/:vid/teachers" element={<Teachers></Teachers>}></Route>
      <Route path="/manage/vendors/:vid/teachers/:tid" element={<Teacher></Teacher>}></Route>
      <Route path="/manage/vendors/:vid/students" element={<Students></Students>}></Route>
      <Route path="/manage/vendors/:vid/students/:sid" element={<Student></Student>}></Route>
      <Route path="/manage/vendors/:vid/students/:sid/works" element={<Works></Works>}></Route>
      <Route path="/manage/vendors/:vid/students/:sid/works/add" element={<AddWorks></AddWorks>}></Route>
      <Route path="/manage/vendors/:vid/students/:sid/works/:pid" element={<Scratch></Scratch>}></Route>
      <Route path="/manage/vendors/:vid/groupworks" element={<GroupWorks></GroupWorks>}></Route>
      <Route path="/works" element={<Works></Works>}></Route>
      <Route path="/works/:pid" element={<Scratch></Scratch>}></Route>

      <Route path="/manage/schoolrooms" element={<Schoolrooms></Schoolrooms>}></Route>
      <Route path="/manage/teachers" element={<Teachers></Teachers>}></Route>
      <Route path="/manage/students" element={<Students></Students>}></Route>

      <Route path={"/manage/registration"} element={<Registration></Registration>}></Route>
      <Route path={"/manage/coursewares"} element={<Coursewares></Coursewares>}></Route>
      <Route path={"/manage/coursewares/:pid"} element={<Scratch canSave={false}></Scratch>}></Route>

      <Route path={"/manage/logs"} element={<Logs></Logs>}></Route>

      {/*<Route path="/manage/schoolrooms/:sid" element={<Schoolroom></Schoolroom>}></Route>*/}
      {/*<Route path={"/manage/scratch"} element={<Scratch></Scratch>}></Route>*/}
      {/*<Route path={"/projects"} element={<Projects/>}></Route>*/}
      {/*<Route path={"/player"} element={<Player></Player>}></Route>*/}
      {/*<Route path={"/protected"} element={<Protected></Protected>}></Route>*/}
    </Routes></GlobalContext.Provider></ChakraProvider></BrowserRouter>
  )
};

export default App;
